import { css } from 'styled-components';
import { pathOr, path } from 'ramda';
const colorShade = new Map([
    ['highEmphasis', 1],
    ['mediumEmphasis', 0.72],
    ['lowEmphasis', 0.42],
    ['focused', 0.16],
    ['outline', 0.14],
    ['pressed', 0.12],
    ['selected', 0.08],
    ['hover', 0.04],
    ['border', 0.07],
    ['default', 0]
]);
const getRGBArray = (color) => color.match(/\d{1,3}(?:\.\d+)?/g);
export const getColor = (colorName, shade) => ({ theme }) => {
    if (shade) {
        return `color-mix(in srgb, ${theme.colors[colorName]} ${colorShade.get(shade) * 100}%, ${theme.colors.surface})`;
    }
    return pathOr('rgb(255, 255, 255)', ['colors', colorName], theme);
};
export const getChartColor = (index) => ({ theme }) => pathOr('', ['chartColors', index], theme);
export const getColorShade = (color, shade) => ({ theme }) => {
    var _a;
    const currentColor = getColor(color)({ theme });
    const result = (_a = getRGBArray(currentColor)) === null || _a === void 0 ? void 0 : _a.join(' ');
    const f = (shade) => { var _a; return (_a = colorShade.get(shade)) !== null && _a !== void 0 ? _a : colorShade.get('default'); };
    return `rgb(${result} / ${f(shade)})`;
};
/**
 *
 * @description: Generates range data of colors
 * @param {ThemeColor} color - any color from theme
 * @param {GradientMapT[]} gradientMap - min size 2
 * @returns {string}
 */
export const getGradient = (color, gradientMap) => ({ theme }) => {
    var _a;
    const currentColor = getColor(color)({ theme });
    const result = (_a = getRGBArray(currentColor)) === null || _a === void 0 ? void 0 : _a.join(' ');
    return gradientMap
        .map((item) => {
        return `rgb(${result} / ${item.opacity}) ${item.gradientSlicePercent}%`;
    })
        .join(',');
};
export const getSizeUnit = ({ theme }) => pathOr(4, ['sizeUnit'], theme);
const SIZE_MODIFICATORS_DICT = new Map([
    ['small', 0.77],
    ['medium', 1],
    ['large', 1.33]
]);
export const getSizeBy = (multiplier = 1, componentSize) => (props) => {
    if (componentSize === 'extraLarge') {
        componentSize = 'large';
        console.error("Size 'extraLarge' is now deprecated, using 'extraLarge' will render 'large' component");
    }
    const size = getSizeUnit(props) * multiplier;
    if (componentSize) {
        const sizeModificator = SIZE_MODIFICATORS_DICT.get(componentSize) || 1;
        return `${Math.ceil(size * sizeModificator)}px`;
    }
    return `${size}px`;
};
const THICKNESS_SIZE_MAP = new Map([
    ['none', 0],
    ['small', 1],
    ['medium', 2],
    ['large', 4]
]);
export const getBorderThickness = () => ({ theme }) => {
    const value = pathOr('small', ['widgetBorderThickness'], theme);
    if (typeof value === 'number')
        return `${value}px`;
    return `${THICKNESS_SIZE_MAP.get(value)}px`;
};
export const getWidgetShadowOptions = ({ theme }) => {
    const shadowOptions = theme === null || theme === void 0 ? void 0 : theme.shadow;
    if (shadowOptions) {
        const shadowColor = getColorShade(shadowOptions.color, shadowOptions.shade)({ theme });
        const { xOffset, yOffset, blur, spread } = shadowOptions;
        return `${shadowColor} ${xOffset}px ${yOffset}px ${blur}px ${spread}px`;
    }
    return 'none';
};
export const getThemeProp = (themeProp) => ({ theme }) => pathOr(undefined, [themeProp], theme);
export const getThemePropOrElse = (themeProps, defaultValue) => ({ theme }) => pathOr(defaultValue, themeProps, theme);
export const getPropOrElse = (pathName, defaultValue) => (props) => pathOr(defaultValue, pathName, props);
export const ifPropExist = (pathName, returnValue) => (props) => {
    const prop = path(pathName, props);
    return prop !== undefined && returnValue;
};
const ELEVATION_DICT = new Map([
    [1, [[0, 1, 1, 0.06]]],
    [
        2,
        [
            [0, 1, 1, 0.06],
            [0, 3, 6, 0.04]
        ]
    ],
    [
        4,
        [
            [0, 1, 1, 0.06],
            [0, 3, 8, 0.12]
        ]
    ],
    [
        8,
        [
            [0, 1, 1, 0.06],
            [0, 3, 8, 0.12],
            [0, 8, 12, 0.12]
        ]
    ],
    [
        16,
        [
            [0, 12, 24, 0.16],
            [0, 8, 12, 0.12],
            [0, 1, 1, 0.06],
            [0, 3, 8, 0.12]
        ]
    ]
]);
const convertToBoxShadowStr = (boxShadowData, rgb) => {
    const pixels = boxShadowData
        .slice(0, 3)
        .map((pix) => `${pix}px`)
        .join(' ');
    const opacity = boxShadowData[3];
    return `${pixels} rgb(${rgb} / ${opacity})`;
};
export const getElevation = (elevation) => ({ theme }) => {
    var _a, _b;
    if (elevation === 0)
        return;
    const shadowColor = getColor('shadows')({ theme });
    const rgb = (_a = getRGBArray(shadowColor)) === null || _a === void 0 ? void 0 : _a.join(' ');
    return (_b = ELEVATION_DICT.get(elevation)) === null || _b === void 0 ? void 0 : _b.map((boxShadowData) => convertToBoxShadowStr(boxShadowData, rgb)).join(', ');
};
export const getFontFamily = (fontKey) => ({ theme }) => { var _a; return `${pathOr((_a = theme === null || theme === void 0 ? void 0 : theme.fonts) === null || _a === void 0 ? void 0 : _a.primary.family, ['fonts', fontKey, 'family'], theme)}, sans-serif`; };
const fontSrcFormatCb = (src) => {
    const urlLink = src.link.includes('http') ? src.link : `${src.baseUrl}${src.link}`;
    return `
@font-face {
  font-family: ${src.family};
  font-style: normal;
  font-display: swap;
  font-weight: ${src.weight};
  src: url(${urlLink}) format('${src.format}');
}
`;
};
export const injectFontFaces = (baseUrl = '') => ({ theme }) => {
    const { fonts } = theme;
    const { primary, secondary } = fonts;
    const fontFacesPrimary = primary.sources
        .map((src) => ({ ...src, family: primary.family, baseUrl }))
        .map(fontSrcFormatCb)
        .join('\n');
    const fontFacesSecondary = secondary
        ? secondary.sources
            .map((src) => ({ ...src, family: secondary.family, baseUrl }))
            .map(fontSrcFormatCb)
            .join('\n')
        : '';
    return css `
      ${fontFacesPrimary}
      ${fontFacesSecondary}
    `;
};
export const getTypography = (name) => ({ theme }) => {
    const themedTypography = path(['typography', name], theme);
    return themedTypography !== null && themedTypography !== void 0 ? themedTypography : {};
};
export const getMixinFromTheme = (name) => ({ theme }) => {
    const mixinsFromTheme = path(['themeMixins', name], theme);
    return mixinsFromTheme !== null && mixinsFromTheme !== void 0 ? mixinsFromTheme : {};
};
export const getHintColor = (type) => {
    switch (type) {
        case 'success': {
            return getColorShade('success', 'highEmphasis');
        }
        case 'warning': {
            return getColorShade('warning', 'highEmphasis');
        }
        case 'error': {
            return getColorShade('danger', 'highEmphasis');
        }
        case 'info': {
            return getColorShade('onSurface', 'highEmphasis');
        }
        default: {
            return getColorShade('primary', 'highEmphasis');
        }
    }
};
